import moment from 'moment-timezone'
import * as R from 'ramda'
import React from 'react'
import styled, { css } from 'styled-components'

import { PROJECT_STATUS, PROJECT_TYPE } from '../../constants'
import { useTranslation } from '../../i18n'
import { getColor, getFontSize } from '../../utils/cssUtils'
import formatter from '../../utils/formatter'
import mq from '../../utils/mediaQuery'
import DonorAvatars from '../DonorAvatars'
import Flex from '../Layout/Flex'
import Progress from '../Progress'
import FundraisingProgressSkeleton from '../Skeleton/FundraisingProgress'

const StatLabel = styled.span`
  color: ${getColor('lightGrey')};
  white-space: nowrap;
  margin-right: 4px;
`
const StatValue = styled.span`
  color: #7b7b7b;
  white-space: nowrap;
  font-weight: bold;
  ${({ ended }) =>
    ended &&
    css`
      color: ${getColor('theme')};
    `}
`
const FundraisingProgress = styled.div``
const Separator = styled.div`
  display: flex;
  align-items: center;
  &::after {
    content: '';
    display: block;
    width: 2px;
    height: 0.8em;
    margin: 0 12px;
    background-color: ${getColor('lightGrey')};
  }
`

const StyledProgress = styled(Progress)`
  margin-bottom: 16px;
`
const Stats = styled.div`
  line-height: 1;
  ${mq.mobile} {
    margin-top: 6px;
  }
`
const Donor = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 32px;
  margin-bottom: 16px;
  ${mq.mobile} {
    margin-bottom: 21px;
  }
`
const DonatedAmt = styled.span`
  display: inline-block;
  font-size: ${getFontSize('headline')};
  font-weight: 600;
  line-height: 1.5;
  color: ${getColor('theme')};
  margin-right: 16px;
  ${mq.mobile} {
    font-size: ${getFontSize('body2')};
  }
`
const DonationCount = styled.span`
  display: inline-block;
  font-size: ${getFontSize('headline')};
  font-weight: 600;
  line-height: 1;
  color: ${getColor('grey')};
  ${mq.tablet} {
    span {
      color: #33485f;
    }
  }
  ${mq.mobile} {
    font-size: ${getFontSize('body2')};
    span {
      color: #33485f;
    }
  }
`
const NoStat = styled.span`
  display: inline-block;
  font-size: ${getFontSize('headline')};
  font-weight: 600;
  line-height: 26px;
`

const FundraisingHeroProgress = ({
  startTime,
  donationTarget,
  donors,
  donorCount,
  projectType,
  projectStatus,
  projectDetailHeroStatusDescription,
  donationCount,
  totalDonatedAmount,
  ...props
}) => {
  const { t } = useTranslation()
  const fundraisedAmt = totalDonatedAmount / 100
  const targetAmt = parseInt(donationTarget / 100)
  const { label: statusLabel, value: statusValue } =
    projectDetailHeroStatusDescription || {}

  const fundraisedPercentage = parseInt((fundraisedAmt / targetAmt) * 100, 10)
  const haveTarget = R.equals(projectType, PROJECT_TYPE.WITH_TARGET)
  const showFundraisedAmt = R.where({
    fundraisedAmt: R.gt(R.__, 0),
    projectStatus: R.complement(R.equals(PROJECT_STATUS.COMING_SOON))
  })({ fundraisedAmt, projectStatus })
  const showDonationCount = R.where({
    projectStatus: R.complement(R.equals(PROJECT_STATUS.COMING_SOON)),
    donationCount: R.gte(R.__, 5)
  })({ projectStatus, donationCount })
  const targetPercentage = parseInt(
    (fundraisedAmt / (targetAmt || 1)) * 100,
    10
  )
  const showPercentage = R.where({
    projectType: R.equals(PROJECT_TYPE.WITH_TARGET),
    targetPercentage: R.gt(R.__, 1)
  })({ projectType, targetPercentage })
  const endedWithZeroDonation = R.where({
    fundraisedAmt: R.equals(0),
    projectStatus: R.equals(PROJECT_STATUS.ENDED)
  })({ fundraisedAmt, projectStatus })

  return R.isNil(totalDonatedAmount) ? (
    <FundraisingProgressSkeleton />
  ) : (
    <>
      <FundraisingProgress>
        <Donor>
          <Stats>
            {showFundraisedAmt && (
              <DonatedAmt>
                {formatter.toCurrency(fundraisedAmt)}
                <span
                  css={`
                    font-size: ${getFontSize('body2')};
                    color: ${getColor('grey')};
                  `}
                >
                  {' ' + t('featuredProject.fundraised')}
                </span>
              </DonatedAmt>
            )}
            {showDonationCount && (
              <DonationCount>
                {formatter.toNumber(donationCount)}
                <span
                  css={`
                    font-size: ${getFontSize('body2')};
                  `}
                >
                  {' ' + t('featuredProject.donations')}
                </span>
              </DonationCount>
            )}
            {!showFundraisedAmt &&
              R.cond([
                [
                  R.equals(PROJECT_STATUS.COMING_SOON),
                  R.always(
                    <NoStat
                      css={`
                        color: ${getColor('theme')}};
                      `}
                    >
                      {t('project.willStartOn', {
                        startDate: moment(startTime).format('YYYY.MM.DD')
                      })}
                    </NoStat>
                  )
                ],
                [
                  R.equals(PROJECT_STATUS.ENDED),
                  R.always(
                    <NoStat
                      css={`
                        color: ${getColor('lightGrey')};
                      `}
                    >
                      {t(statusValue)}
                    </NoStat>
                  )
                ],
                [
                  R.T,
                  R.always(
                    <NoStat
                      css={`
                        color: ${getColor('theme')};
                      `}
                    >
                      {t('project.beTheFirstDonor')}
                    </NoStat>
                  )
                ]
              ])(projectStatus)}
          </Stats>
          {!R.isEmpty(donors) &&
            projectStatus !== PROJECT_STATUS.COMING_SOON &&
            donationCount > 0 && (
              <DonorAvatars avatars={donors} donorCount={donorCount} />
            )}
        </Donor>
        {haveTarget && !endedWithZeroDonation && (
          <StyledProgress percent={targetAmt ? targetPercentage : 0} />
        )}
      </FundraisingProgress>
      {!endedWithZeroDonation && (
        <Flex
          css={`
            min-height: 1.5em;
          `}
        >
          {(haveTarget || showPercentage) && (
            <Flex
              css={`
                flex-grow: 1;
              `}
            >
              {haveTarget && (
                <Flex>
                  <StatLabel>{t('featuredProject.target')}</StatLabel>
                  <StatValue>{formatter.toCurrency(targetAmt)}</StatValue>
                </Flex>
              )}
              {showPercentage && (
                <>
                  <Separator />
                  <Flex>
                    <StatLabel>
                      {t('featuredProject.percentTargetCompleted')}
                    </StatLabel>
                    <StatValue>{fundraisedPercentage + '%'}</StatValue>
                  </Flex>
                </>
              )}
            </Flex>
          )}
          <Flex>
            {statusLabel && <StatLabel>{t(statusLabel)}</StatLabel>}
            <StatValue ended={projectStatus === PROJECT_STATUS.ENDED}>
              {t(statusValue)}
            </StatValue>
          </Flex>
        </Flex>
      )}
    </>
  )
}

export default React.memo(FundraisingHeroProgress)
